import React, { useEffect, useState } from "react";
import {
  Modal,
  Input,
  InputNumber,
  Select,
  Row,
  Col,
  message,
  Divider,
  DatePicker
} from "antd";
import moment from "moment";
import "moment/locale/fr";
import { createProspection, updateProspection } from "../../api/prospections";
import { get_ListeClients } from "../../api/clients";
import { get_ListeRegion } from "../../api/region";

const { TextArea } = Input;
const { Option } = Select;

export default function ModalProspectionForm({ open, onClose, onSuccess, prospectionToEdit = null }) {
  const [loading, setLoading] = useState(false);
  const [listeClients, setListeClients] = useState([]);
  const [listeRegion, setListeRegion] = useState([]);
  const isEditMode = !!prospectionToEdit;
  /** ✅ STATE UNIQUE */
  const [currentProspection, setCurrentProspection] = useState({
    client_id: null,
    marchandise: "",
    region_depart: null,
    region_arrive: null,
    date_depart: null,
    date_arrive: null,
    poids: null,
    distance: null,
    type_tc: null,
    nb_tc: null,
    montant_estime: null,
    priorite: "MOYENNE",
    description: "",
    interlocuteur_nom: "",
    interlocuteur_poste: "",
    interlocuteur_tel: "",
    interlocuteur_email: "",
    user_id: localStorage.getItem('user_id'),
    service: null
  });

  /* ========================= DATA ========================= */

  useEffect(() => {
    fetchClients();
    fetchRegions();
  }, []);

  useEffect(() => {
    if (prospectionToEdit) {
      setCurrentProspection({
        ...prospectionToEdit,

        // ✅ conversion string Flask → moment
        date_depart: prospectionToEdit.date_depart
          ? moment(prospectionToEdit.date_depart)
          : null,

        date_arrive: prospectionToEdit.date_arrive
          ? moment(prospectionToEdit.date_arrive)
          : null,

        date_prochaine_relance: prospectionToEdit.date_prochaine_relance
          ? moment(prospectionToEdit.date_prochaine_relance)
          : null,

        // ✅ montant en number
        montant_estime: prospectionToEdit.montant_estime
          ? Number(prospectionToEdit.montant_estime)
          : null,
      });
    }
  }, [prospectionToEdit]);


  const fetchClients = async () => {
    try {
      const res = await get_ListeClients();
      setListeClients(res.data);
    } catch (e) {
      message.error("Erreur chargement clients");
    }
  };

  const fetchRegions = async () => {
    try {
      const res = await get_ListeRegion();
      setListeRegion(res.data);
    } catch (e) {
      message.error("Erreur chargement régions");
    }
  };

  /* ========================= HANDLER ========================= */

  const handleChange = (field, value) => {
    setCurrentProspection((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  /* ========================= SUBMIT ========================= */

  const submit = async () => {
    if (!currentProspection.client_id) {
      message.warning("Veuillez sélectionner un client");
      return;
    }

    try {
      setLoading(true);

      // ✅ payload clean
      const payload = {
        ...currentProspection,

        date_depart: currentProspection.date_depart
          ? currentProspection.date_depart.format("YYYY-MM-DD HH:mm:ss")
          : null,

        date_arrive: currentProspection.date_arrive
          ? currentProspection.date_arrive.format("YYYY-MM-DD HH:mm:ss")
          : null,
      };

      if (prospectionToEdit) {
        console.log("update");
        console.log(payload);
        // ✅ UPDATE
        await updateProspection(payload);
        message.success("Prospection mise à jour avec succès !");
      } else {
        console.log("create");
        // ✅ CREATE
        await createProspection(payload);
        message.success("Prospection créée avec succès !");
      }

      onSuccess();
      onClose();
    } catch (e) {
      message.error("Erreur lors de l'enregistrement");
    } finally {
      setLoading(false);
    }
  };

  /* ========================= UI ========================= */

  return (
    <Modal
      open={open}
      title={
        isEditMode
          ? "✏️ Modifier la prospection"
          : "📦 Nouvelle prospection transport"
      }
      onCancel={onClose}
      onOk={submit}
      okText={isEditMode ? "Mettre à jour" : "Créer"}
      confirmLoading={loading}
      width={900}
      style={{ top: 8 }}
    >
      {/* ================= CLIENT ================= */}
      <Divider orientation="left">Client / Marchandise</Divider>

      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Select
          showSearch
            optionFilterProp="children"
            size="large"
            placeholder="Service"
            style={{ width: "100%" }}
            value={currentProspection.service}
            onChange={(v) => handleChange("service", v)}
          >
           <Option key="1" value="transport">Transport</Option>
           <Option key="2" value="transit">Transit</Option>
           <Option key="3" value="transit_transport">Transit & Transport</Option>
          </Select>
        </Col>
        <Col span={12}>
          <Select
            showSearch
            optionFilterProp="children"
            size="large"
            placeholder="Client"
            style={{ width: "100%" }}
            value={currentProspection.client_id}
            onChange={(v) => handleChange("client_id", v)}
          >
            {listeClients.map((c) => (
              <Option key={c.id} value={c.id}>
                {c.nom}
              </Option>
            ))}
          </Select>
        </Col>

        <Col span={12}>
          <Input
            size="large"
            placeholder="Marchandise"
            value={currentProspection.marchandise}
            onChange={(e) =>
              handleChange("marchandise", e.target.value)
            }
          />
        </Col>
      </Row>

      {/* ================= INTERLOCUTEUR ================= */}
      <Divider orientation="left">Interlocuteur</Divider>

      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Input
            placeholder="Nom de l'interlocuteur"
            value={currentProspection.interlocuteur_nom}
            onChange={(e) =>
              handleChange("interlocuteur_nom", e.target.value)
            }
          />
        </Col>

        <Col span={12}>
          <Input
            placeholder="Poste / Fonction"
            value={currentProspection.interlocuteur_poste}
            onChange={(e) =>
              handleChange("interlocuteur_poste", e.target.value)
            }
          />
        </Col>

        <Col span={12}>
          <Input
            placeholder="Téléphone"
            value={currentProspection.interlocuteur_tel}
            onChange={(e) =>
              handleChange("interlocuteur_tel", e.target.value)
            }
          />
        </Col>

        <Col span={12}>
          <Input
            placeholder="Email"
            type="email"
            value={currentProspection.interlocuteur_email}
            onChange={(e) =>
              handleChange("interlocuteur_email", e.target.value)
            }
          />
        </Col>
      </Row>

      {/* ================= TRAJET ================= */}
      <Divider orientation="left">Trajet</Divider>
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <DatePicker
            showTime
            size="small"
            format="YYYY-MM-DD HH:mm:ss"
            placeholder="Date de départ"
            style={{ width: "100%", textAlign: "center" }}
            value={
              currentProspection.date_depart
            }
            onChange={(date) =>
              handleChange("date_depart", date)
            }
          />
        </Col>

        <Col span={12}>
          <DatePicker
            showTime
            size="small"
            format="YYYY-MM-DD HH:mm:ss"
            placeholder="Date d'arrive"
            style={{ width: "100%", textAlign: "center" }}
            value={
              currentProspection.date_arrive
            }
            onChange={(date) =>
              handleChange("date_arrive", date)
            }
          />

        </Col>
        <Col span={12}>
          <Select
          showSearch
            optionFilterProp="children"
            size="large"
            placeholder="Région de départ"
            style={{ width: "100%" }}
            value={currentProspection.region_depart}
            onChange={(v) => handleChange("region_depart", v)}
          >
            {listeRegion.map((r) => (
              <Option key={r.id} value={r.id}>
                {r.nom}
              </Option>
            ))}
          </Select>
        </Col>

        <Col span={12}>
          <Select
          showSearch
            optionFilterProp="children"
            size="large"
            placeholder="Région d'arrivée"
            style={{ width: "100%" }}
            value={currentProspection.region_arrive}
            onChange={(v) => handleChange("region_arrive", v)}
          >
            {listeRegion.map((r) => (
              <Option key={r.id} value={r.id}>
                {r.nom}
              </Option>
            ))}
          </Select>
        </Col>

        <Col span={12}>
          <InputNumber
            placeholder="Distance (km)"
            style={{ width: "100%" }}
            value={currentProspection.distance}
            onChange={(v) => handleChange("distance", v)}
          />
        </Col>

        <Col span={12}>
          <InputNumber
            placeholder="Poids (kg)"
            style={{ width: "100%" }}
            value={currentProspection.poids}
            onChange={(v) => handleChange("poids", v)}
          />
        </Col>
      </Row>

      {/* ================= CONTENEURS ================= */}
      <Divider orientation="left">Conteneurs</Divider>

      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Select
          showSearch
            optionFilterProp="children"
            size="large"
            placeholder="Type de conteneur"
            style={{ width: "100%", textAlign: "center" }}
            value={currentProspection.type_tc}
            onChange={(v) => handleChange("type_tc", v)}
            allowClear
          >
            <Option value="20 pieds">20 pieds</Option>
            <Option value="40 pieds">40 pieds</Option>
            <Option value="Bennes">Bennes</Option>

          </Select>
        </Col>

        <Col span={12}>
          <InputNumber
            size="small"
            placeholder="Nombre de TC"
            style={{ width: "100%" }}
            value={currentProspection.nb_tc}
            onChange={(v) => handleChange("nb_tc", v)}
          />
        </Col>
      </Row>

      {/* ================= COMMERCIAL ================= */}
      <Divider orientation="left">Commercial</Divider>

      <Row gutter={[16, 16]}>
        <Col span={12}>
          <InputNumber
            size="small"
            placeholder="Montant estimé"
            style={{ width: "100%" }}
            addonAfter="Ar"
            min={0}
            value={currentProspection.montant_estime}
            onChange={(v) =>
              handleChange("montant_estime", v)
            }
          />
        </Col>

        <Col span={12}>
          <Select
            showSearch
            optionFilterProp="children"
            size="large"
            value={currentProspection.priorite}
            style={{ width: "100%", textAlign: "center" }}
            onChange={(v) => handleChange("priorite", v)}
          >
            <Option value="BASSE">Basse</Option>
            <Option value="MOYENNE">Moyenne</Option>
            <Option value="HAUTE">Haute</Option>
          </Select>
        </Col>
      </Row>

      {/* ================= NOTES ================= */}
      <Divider orientation="left">Notes</Divider>

      <TextArea
        rows={3}
        placeholder="Description / contexte commercial"
        value={currentProspection.description}
        onChange={(e) =>
          handleChange("description", e.target.value)
        }
      />
    </Modal>
  );
}
